import React, { type ReactNode, type SVGProps } from 'react';
import Text from '@ingka/text';
import { styled } from 'styled-components';
import InlineMessage from '@ingka/inline-message';
import colors from '../styles/colors';
import { HelperText } from '@ingka/helper-text';

const StyledText = styled(Text)`
  &&& {
    display: flex;
    flex-direction: column;
    white-space: pre-wrap;
    margin: 0;
    font-size: 1rem;
  }
`;

const StyledMessage = styled(HelperText)`
  &&& {
    font-size: 1rem;
  }
`;

const InlineMessageStyled = styled(InlineMessage)`
  &&& {
    svg {
      fill: ${colors.icon.default};
    }

    display: inline-flex;
    align-items: center;
    margin-bottom: 1rem;
  }
`;

interface InfoMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: (prefix?: string | undefined) => SVGProps<SVGElement>[];
  iconAriaLabel: string;
  message: Array<ReactNode>;
  id: string;
  title?: string;
}

const InfoMessage: React.FC<InfoMessageProps> = ({
  icon,
  iconAriaLabel,
  message,
  id,
  title,
  ...rest
}) => {
  const messageText = message
    .filter((textLine) => {
      if (textLine && typeof textLine === 'string') {
        return textLine.trim().length > 0;
      }
      return !!textLine;
    })
    .map((textLine) => {
      if (typeof textLine === 'string') {
        return title ? (
          <StyledMessage key={textLine}>{`${textLine}\n`}</StyledMessage>
        ) : (
          <span key={textLine}>{`${textLine}\n`}</span>
        );
      }
      return textLine;
    });

  const messageBody = (
    <StyledText tagName="span">
      {title && <span>{title}</span>}
      {messageText}
    </StyledText>
  );

  return (
    <InlineMessageStyled
      {...rest}
      ssrIcon={icon}
      labelMessageType={iconAriaLabel}
      subtle
      variant="informative"
      body={messageBody}
      data-cy={`confirm-info-${id}`}
    />
  );
};

export default InfoMessage;
